import { BACKEND_URL as API_URL } from '@/config/api';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, Bell, BellOff, Phone, Mail, AlertTriangle, CheckCircle2, ShieldAlert, FileText, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';

const NOTIF_TYPES = [
  { key: 'new_samples', label: 'Nye prøveresultater', desc: 'Varsling når nye prøver er klare for dine områder', icon: FileText },
  { key: 'critical_alerts', label: 'Kritiske varsler', desc: 'Umiddelbar varsling ved raudt eller oransje nivå', icon: ShieldAlert },
  { key: 'weekly_summary', label: 'Ukentleg oppsummering', desc: 'Samanfatning av vekens data kvar måndag', icon: CalendarDays },
];

export default function NotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [settings, setSettings] = useState({
    sms_notifications_enabled: false,
    email_notifications_enabled: false,
    phone_number: null,
    email: null,
    has_verified_phone: false,
    notification_preferences: { new_samples: true, critical_alerts: true, weekly_summary: false },
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/users/me/notification-settings`, { withCredentials: true });
      setSettings(res.data);
    } catch {
      toast.error('Kunne ikkje hente varslingsinnstillingar');
    } finally { setLoading(false); }
  };

  const update = async (payload, key) => {
    setSaving(key);
    try {
      await axios.put(`${API_URL}/api/users/me/notification-settings`, payload, { withCredentials: true });
      setSettings(prev => ({ ...prev, ...payload, ...(payload.notification_preferences ? { notification_preferences: { ...prev.notification_preferences, ...payload.notification_preferences } } : {}) }));
      toast.success('Innstillingar oppdatert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikkje oppdatere');
    } finally { setSaving(null); }
  };

  const toggleSms = () => {
    if (!settings.has_verified_phone && !settings.sms_notifications_enabled) {
      toast.error('Du må først aktivere SMS 2FA for å motta varsler', { description: 'Gå til Innstillinger > To-faktor autentisering' });
      return;
    }
    update({ sms_notifications_enabled: !settings.sms_notifications_enabled }, 'sms');
  };

  const toggleEmail = () => update({ email_notifications_enabled: !settings.email_notifications_enabled }, 'email');

  const togglePref = (key) => {
    const newVal = !settings.notification_preferences[key];
    update({ notification_preferences: { [key]: newVal } }, key);
  };

  if (loading) {
    return (
      <Card className="border border-slate-200"><CardContent className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></CardContent></Card>
    );
  }

  const anyChannelOn = settings.sms_notifications_enabled || settings.email_notifications_enabled;

  return (
    <div className="space-y-5" data-testid="notification-settings">
      {/* Channels Card */}
      <Card className="border border-slate-200 hover:border-teal-200 transition-colors">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${anyChannelOn ? 'bg-teal-600' : 'bg-slate-400'}`}>
              {anyChannelOn ? <Bell className="w-5 h-5 text-white" /> : <BellOff className="w-5 h-5 text-white" />}
            </div>
            <div>
              <CardTitle className="text-lg">Varslingskanalar</CardTitle>
              <CardDescription>Vel korleis du vil motta varslar</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* SMS Channel */}
          <div className="flex items-center justify-between p-3.5 rounded-lg border border-slate-100 bg-slate-50/50" data-testid="sms-channel-row">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${settings.has_verified_phone ? 'bg-sky-100' : 'bg-slate-100'}`}>
                <Phone className={`w-4 h-4 ${settings.has_verified_phone ? 'text-sky-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <Label className="font-medium text-slate-800 text-sm cursor-pointer">SMS-varsling</Label>
                <p className="text-xs text-slate-500">
                  {settings.phone_number ? `Telefon: ${settings.phone_number}` : 'Ingen telefon registrert'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.sms_notifications_enabled}
              onCheckedChange={toggleSms}
              disabled={saving === 'sms' || !settings.has_verified_phone}
              data-testid="sms-toggle"
            />
          </div>

          {/* Email Channel */}
          <div className="flex items-center justify-between p-3.5 rounded-lg border border-slate-100 bg-slate-50/50" data-testid="email-channel-row">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-indigo-100">
                <Mail className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <Label className="font-medium text-slate-800 text-sm cursor-pointer">E-postvarsling</Label>
                <p className="text-xs text-slate-500">{settings.email || 'Ingen e-post'}</p>
              </div>
            </div>
            <Switch
              checked={settings.email_notifications_enabled}
              onCheckedChange={toggleEmail}
              disabled={saving === 'email'}
              data-testid="email-toggle"
            />
          </div>

          {!settings.has_verified_phone && (
            <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                For SMS-varslar treng du eit verifisert telefonnummer. Gå til <strong>To-faktor autentisering</strong> for å registrere.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Types Card */}
      <Card className="border border-slate-200 hover:border-teal-200 transition-colors">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Varslingstypar</CardTitle>
          <CardDescription>Vel kva hendingar du vil bli varsla om</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {NOTIF_TYPES.map(({ key, label, desc, icon: Icon }) => (
            <div key={key} className="flex items-center justify-between p-3.5 rounded-lg border border-slate-100 bg-slate-50/50" data-testid={`pref-${key}-row`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-100">
                  <Icon className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <Label className="font-medium text-slate-800 text-sm cursor-pointer">{label}</Label>
                  <p className="text-xs text-slate-500">{desc}</p>
                </div>
              </div>
              <Switch
                checked={settings.notification_preferences[key] ?? false}
                onCheckedChange={() => togglePref(key)}
                disabled={saving === key || !anyChannelOn}
                data-testid={`pref-${key}-toggle`}
              />
            </div>
          ))}

          {!anyChannelOn && (
            <div className="flex items-start gap-2.5 p-3 bg-slate-100 border border-slate-200 rounded-lg">
              <BellOff className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600">
                Aktiver minst ein varslingskanal (SMS eller e-post) for å styre varslingstypar.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Summary */}
      {anyChannelOn && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4" data-testid="notification-summary">
          <h4 className="font-medium text-teal-800 text-sm mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Aktive varslar
          </h4>
          <div className="flex flex-wrap gap-2">
            {settings.sms_notifications_enabled && (
              <span className="text-xs bg-teal-100 text-teal-700 px-2.5 py-1 rounded-full font-medium">SMS</span>
            )}
            {settings.email_notifications_enabled && (
              <span className="text-xs bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full font-medium">E-post</span>
            )}
            {Object.entries(settings.notification_preferences).filter(([, v]) => v).map(([k]) => {
              const t = NOTIF_TYPES.find(n => n.key === k);
              return t ? <span key={k} className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">{t.label}</span> : null;
            })}
          </div>
        </div>
      )}
    </div>
  );
}
